import type { Bucket } from '@enonic-types/core';
import type {
	AlertBannerCardProps,
} from '/types/alerts.d';

import React from 'react';
import { faXmarkLarge } from '@fortawesome/pro-light-svg-icons';
import { Paginator } from '@hdir/ui/paginator';
import { Select, SelectOption } from '@hdir/ui/select';

import { P } from '/react4xp/components/Paragraph';
import { Button } from '/react4xp/components/Button';
import { AlertBannerCard } from '/react4xp/shared/AlertBannerCard/AlertBannerCard';
import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';
import { NoActiveAlerts } from '/react4xp/shared/NoActiveAlerts/NoActiveAlerts';
import { Ul } from '/react4xp/components/List';

import './alert-list.scss';
import {
	ITEMS_PER_PAGE,
	useExpiredAlertsState,
} from './useExpiredAlertsState';


export function ExpiredAlerts({
	countyBuckets,
	drugTypeBuckets,
	expiredAlerts,
	expiredAlertsCount,
	graphqlServiceUrl,
	locale,
	initialCountyFromParams,
	initialDrugTypeFromParams,
}: {
	countyBuckets: Bucket[];
	drugTypeBuckets: Bucket[];
	expiredAlerts: AlertBannerCardProps[];
	expiredAlertsCount: number;
	graphqlServiceUrl: string;
	locale: string;
	initialCountyFromParams?: string,
	initialDrugTypeFromParams?: string,
}): React.JSX.Element {

	const {
		activePage,
		countyFilterList,
		countyOnChange,
		countyValue,
		currentItems,
		drugTypeOnChange,
		drugTypesFilterList,
		drugValue,
		emptyFilters,
		filteredExpiredAlertsCount,
		onPageClick,
		showSpinner,
	} = useExpiredAlertsState({
		countyBuckets,
		drugTypeBuckets,
		expiredAlerts,
		expiredAlertsCount,
		graphqlServiceUrl,
		locale,
		initialCountyFromParams,
		initialDrugTypeFromParams,
	});

	return (
		<div className="flex flex-col gap-md tablet:gap-lg">
			{
				expiredAlertsCount === 0
					? (
						<NoActiveAlerts isActive={false} />
					) : (
						<>
							{(countyFilterList && drugTypesFilterList) ? (
								<div className="flex flex-col gap-md tablet:gap-lg">
									<div className="flex flex-wrap justify-between gap-md tablet:gap-lg">
										<Select
											className="w-full tablet:max-w-[450px]"
											disabled={showSpinner}
											getItemId={(item) => item.key}
											getDisplayName={(item) => `${item.key} (${item.docCount})`}
											value={countyValue ?? null}
											onChange={
												// The FRISK typing is wrong, so needs the as conversion.
												(county) => countyOnChange(county as unknown as string)
											}
											name="county"
											label="Velg fylke"
											placeholder="Alle fylker"
											isRemovable
										>
											{countyFilterList.map(({docCount, key}) => (
												<SelectOption key={key} value={key} disabled={!docCount}>
													{key} ({docCount})
												</SelectOption>
											))}
										</Select>
										<Select
											className="w-full tablet:max-w-[450px]"
											disabled={showSpinner}
											getItemId={(item) => item.key}
											getDisplayName={(item) => `${item.key} (${item.docCount})`}
											value={drugValue ?? null}
											onChange={
												// The FRISK typing is wrong, so needs the as conversion.
												(drugType) => drugTypeOnChange(drugType as unknown as string)}
											name="drugType"
											label="Velg rusmiddeltype"
											placeholder="Alle rusmiddeltyper"
											isRemovable
										>
											{drugTypesFilterList.map(({docCount, key}) => (
												<SelectOption key={key} value={key} disabled={!docCount}>
													{key} ({docCount})
												</SelectOption>
											))}
										</Select>
									</div>
									<Button
										className={`${!countyValue && !drugValue ? 'hidden' : ''}`}
										disabled={showSpinner}
										onClick={() => emptyFilters()}
										variant="secondary"
									>
										<FontAwesomeIconWrapper icon={faXmarkLarge}/> Fjern filter
									</Button>
								</div>
							): null}
							{
								filteredExpiredAlertsCount
									? <P className={showSpinner ? 'text-neutral-disabled' : ''}>{filteredExpiredAlertsCount} tidligere varsler</P>
									: null
							}
							{currentItems ? (
								<Ul id="varsel-liste" className="flex flex-col gap-md tablet:gap-lg">
									{currentItems.map((alert, i) => (
										<li key={alert.id} className="list-none">
											<AlertBannerCard
												alertListLink={alert.alertListLink}
												content={alert.content}
												date={alert.date}
												empty={showSpinner}
												id={alert.id}
												image={alert.image}
												location={alert.location}
												showSpinner={showSpinner && i === 0}
												title={alert.title}
												variant={alert.variant}
												alertType={alert.alertType}
											/>
										</li>
									))}
								</Ul>
							) : null}
							<Paginator
								activePage={activePage}
								ariaControls="varsel-liste"
								className={showSpinner ? 'filter contrast-[0.25] saturate-0 brightness-150 pointer-events-none': ''}
								itemsCount={filteredExpiredAlertsCount}
								itemsPerPage={ITEMS_PER_PAGE}
								onPageClick={value => onPageClick(value)}
							/>
						</>
					)}
		</div>
	);
}
